import { ChangeDetectionStrategy, Component, DestroyRef, input, OnDestroy, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Observable, switchMap, tap } from 'rxjs';
import { BomNotesState, BomNotesStore } from './bom-notes.store';

@Component({
  selector: 'bom-notes',
  templateUrl: './bom-notes.component.html',
  styleUrl: './bom-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DatePipe]
})
// The card follows the bomItemId input: every change re-inits the store (switchMap drops stale in-flight responses)
export class BomNotesComponent implements OnDestroy {
  readonly bomItemId = input.required<number | undefined>();

  // emits the id the note was saved to (captured at click — a mid-flight selection change must not misroute the tree indicator)
  readonly noteAdded = output<number>();

  readonly state$: Observable<BomNotesState>;

  readonly composerOpen = signal(false);

  constructor(
    private store: BomNotesStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    toObservable(this.bomItemId)
      .pipe(
        // a composer opened for one item must not post to the next
        tap(() => this.composerOpen.set(false)),
        switchMap(bomItemId => this.store.init(bomItemId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  // Called from the card header's "+" icon — it lives in the page markup, like the ZAŁĄCZNIKI upload icon
  toggleComposer(): void {
    this.composerOpen.update(open => !open);
  }

  closeComposer(): void {
    this.composerOpen.set(false);
  }

  onSaveClick(content: string): void {
    const bomItemId = this.bomItemId();
    const trimmed = content.trim();

    if (!trimmed || bomItemId === undefined) {
      return;
    }

    this.store.create(bomItemId, trimmed).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // closing removes the textarea and its content with it — kept open (with content) on error
      if (!this.store.state.saveError) {
        this.composerOpen.set(false);
        this.noteAdded.emit(bomItemId);
      }
    });
  }
}
